// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Recording a game as it is played (M3-39, FR-033).
//
// The client half of the replay contract: `PlaySession` reports every action a player takes with
// the step it landed on, and this accumulates them into the `ReplayTrace` that `core/ts/replay`
// can run again. It knows nothing about storage and nothing about the network — it is a list and
// a hash, so a test can play a game and check the trace without either.
//
// **`mark` is called once per simulation step, not once per frame**, and that is the one piece of
// wiring here worth arguing about. A frame covers 33 steps on a phone and 11 on a headset, so a
// checkpoint taken on "the frame that crossed a boundary" lands at step 12,016 on one device and
// 12,004 on another — and two recordings of the same game then carry hashes with nothing to
// compare, which is the entire job of a checkpoint. Asked per step, every device stops at exactly
// 2,000. The cost is one modulo per step against a step that already does a world solve, a rule
// pass and a switch scan.
//
// **Nothing here is allowed to affect the game.** A recorder that could change what it records is
// not a recorder. It is handed numbers and it keeps them.

import type { Game } from '@core/game'
import {
  CHECKPOINT_STEPS,
  CORE_VERSION,
  MAX_TRACE_STEPS,
  checkpointOf,
  type ReplayTrace,
  type TraceAction,
  type TraceCheckpoint,
  type TraceEvent,
} from '@core/replay'
import type { LoadedMachine } from '@core/machine'
import { DEFAULT_INITIALS } from '@core/game'

export interface RecorderOptions {
  readonly seed: number
  readonly playerCount: number
  /**
   * Where initials entry opened, when it was not `DEFAULT_INITIALS` (M2-19a).
   *
   * The recorder is handed it rather than reading it off the `Game`, for the reason the header of
   * this file gives about everything else here: *"Nothing here is allowed to affect the game. It
   * is handed numbers and it keeps them."* It is also a setting rather than a state, so reading it
   * from a game mid-play would be reading it from the wrong place at the wrong time.
   */
  readonly startingInitials?: string
}

export class TraceRecorder {
  private readonly machineId: string
  private readonly machineVersion: string
  private readonly ballsPerGame: number
  private readonly seed: number
  private readonly playerCount: number
  private readonly startingInitials: string | undefined
  private readonly events: TraceEvent[] = []
  private readonly checkpoints: TraceCheckpoint[] = []
  /** The last multiple of `CHECKPOINT_STEPS` already marked, so a stall cannot mark twice. */
  private markedAt = -1
  private steps = 0
  /** True once the trace has hit 10 §3's two-hour cap and stopped accepting anything. */
  private full = false

  constructor(loaded: LoadedMachine, options: RecorderOptions) {
    this.machineId = loaded.definition.machineId
    this.machineVersion = loaded.definition.machineVersion
    this.ballsPerGame = loaded.definition.meta.ballsPerGame
    this.seed = options.seed
    this.playerCount = options.playerCount
    // Normalised to "absent" at the default, so a trace only carries the field when it changes
    // something. `submissionKey` folds it in on the same condition and for the same reason.
    this.startingInitials =
      options.startingInitials === undefined || options.startingInitials === DEFAULT_INITIALS
        ? undefined
        : options.startingInitials
  }

  /**
   * How many actions are held.
   *
   * **The doc here used to say "the settings page shows it", and the settings page does not.**
   * `src/client/routes/Settings.tsx` is real and routed from `main.tsx`; it carries appearance,
   * audio, currency, reduce-flashing and motion, and nothing at all about the recording. So the
   * claim was false about the page's *contents* rather than about the page — the same defect and a
   * different sentence. The first correction written here said "there is no settings page", which
   * was an unchecked claim about a reader, made in the course of fixing unchecked claims about
   * readers. Grep, then write. The Quest client prints this in the save log and nothing else reads
   * it. Said plainly now, because a comment naming a reader that does not have it is how `capped`
   * below stayed unwired for a whole
   * product — `check-reachable` cannot see a class member, so nothing but reading catches it.
   */
  get length(): number {
    return this.events.length
  }

  /**
   * Has the trace reached the cap and stopped recording?
   *
   * **Read by `machineRuntime`, which tells the player.** Past the cap the recorder keeps no
   * further actions, so the game can no longer be posted — `traceProblem` refuses it and `Replay`
   * with it — and can no longer be resumed, because `LocalHistory.resumable` refuses a record whose
   * inputs stopped while its step count went on. Both of those are losses a player is entitled to
   * hear about at the moment they happen rather than at the end of a two-hour game.
   */
  get capped(): boolean {
    return this.full
  }

  /**
   * Keep one action.
   *
   * Past the cap it keeps nothing and says so through `capped`, rather than growing until the
   * tab runs out of memory. 10 §3 rejects an oversize trace outright, so a client that kept
   * recording would be spending a player's battery producing something the server refuses.
   */
  record(step: number, action: TraceAction): void {
    if (this.full) return
    if (step > MAX_TRACE_STEPS) {
      this.full = true
      return
    }
    this.events.push({ step, action })
  }

  /**
   * Take a checkpoint if this step is one, and note how far the game has run.
   *
   * Called **once per simulation step**, not once per frame. A frame covers 33 steps on a phone
   * and 11 on a headset, so a checkpoint taken "on the frame that crossed a boundary" lands on a
   * different step on every device — and two recordings of the same game would then carry hashes
   * with nothing to compare. On the step itself, every device stops at exactly 2,000.
   *
   * One modulo on the steps that are not boundaries, which is all but one in two thousand.
   */
  mark(game: Game): void {
    this.steps = game.step
    if (this.full) return
    if (game.step === 0 || game.step % CHECKPOINT_STEPS !== 0) return
    if (game.step <= this.markedAt) return
    this.markedAt = game.step
    this.checkpoints.push(checkpointOf(game))
  }

  /**
   * The three growing lists, without copying them (M8-18).
   *
   * What `trace-v1` reads four times a second. `trace()` copies both arrays and hunts for the best
   * score, which is right for something written to storage once a minute and wasteful for
   * something asked every 250 ms for the length of a game — a long trace is tens of thousands of
   * events, and copying it 240 times a minute to send sixteen of them is work with no product.
   *
   * The arrays are handed out live and typed `readonly`, which is the honest arrangement: the
   * caller slices a window out of them and keeps nothing, and a recorder that defensively copied
   * for a caller that immediately slices would be copying for nobody.
   */
  get live(): {
    readonly events: readonly TraceEvent[]
    readonly marks: readonly TraceCheckpoint[]
    readonly steps: number
  } {
    return { events: this.events, marks: this.checkpoints, steps: this.steps }
  }

  /** The trace so far, as a document. Safe to call mid-game: this is what a resume saves. */
  trace(game: Game): ReplayTrace {
    return {
      machineId: this.machineId,
      machineVersion: this.machineVersion,
      coreVersion: CORE_VERSION,
      seed: this.seed,
      playerCount: this.playerCount,
      ballsPerGame: this.ballsPerGame,
      ...(this.startingInitials === undefined ? {} : { startingInitials: this.startingInitials }),
      events: [...this.events],
      checkpoints: [...this.checkpoints],
      steps: Math.max(this.steps, game.step),
      claimedScore: bestScore(game),
    }
  }

  /**
   * Adopt a trace that has already been played, so recording continues from its end.
   *
   * The other half of resuming. Without it, a resumed game would record only what happened after
   * the resume — and the trace it saved would replay to a machine in attract mode with a score
   * of nothing, which is a resume that silently throws the game away the second time.
   */
  adopt(trace: ReplayTrace): void {
    this.events.length = 0
    this.checkpoints.length = 0
    this.events.push(...trace.events)
    this.checkpoints.push(...trace.checkpoints)
    this.steps = trace.steps
    this.markedAt = trace.steps - (trace.steps % CHECKPOINT_STEPS)
    this.full = false
  }

  /** Start again. A new game on the same machine is a new trace. */
  reset(): void {
    this.events.length = 0
    this.checkpoints.length = 0
    this.markedAt = -1
    this.steps = 0
    this.full = false
  }
}

/** The best score in the game, which is what a one-player claim is and what a table row wants. */
function bestScore(game: Game): number {
  let best = 0
  for (const player of game.players) best = Math.max(best, player.score)
  return best
}
